
import { useEffect, useState } from "react";
import EmployeeDropdown from "../component/EmployeeDropdown";
import DateRangePickerComponent from "../component/DateRangePickerComponent";
import EmployeeAttendanceDropdown from "../component/EmployeeReportDownload";
import { FaList } from "react-icons/fa";
import { TbLayoutDashboardFilled } from "react-icons/tb";
import CalenderView from "../component/CalenderView";
import TimeLineView from "../component/TimeLineView";
import { jwtDecode } from "jwt-decode";
import Spinner from "../component/Spinner";
import Pagination from "../component/Pagination";
import axiosInstance from "../networking/axiosconfig";
import { FaRegClock } from "react-icons/fa";
import { Toaster, toast } from "react-hot-toast";
import Navbar from './Navbar';
import { Layers, Plus } from 'lucide-react';

export default function AttendanceScreen() {
    const [dateFilter, setDateFilter] = useState({ startDate: "", endDate: "" });
    const [employeeFilter, setEmployeeFilter] = useState([]);
    const [attendanceData, setAttendanceData] = useState([]);
    const [dateRangeLabel, setDateRangeLabel] = useState("");
    const [activeView, setActiveView] = useState("list");
    const [loading, setLoading] = useState(false);
    const [userId, setUserId] = useState(null);
    const [userName, setUserName] = useState("");
    const [role, setRole] = useState(null);
    const [webStatus, setWebStatus] = useState(null);

    const [selectedMonth, setSelectedMonth] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [sortConfig, setSortConfig] = useState({
        key: "first_name",
        direction: "asc",
    });
    const [isCheckedIn, setIsCheckedIn] = useState(false);
    const [checkInTime, setCheckInTime] = useState(null);
    const [checkInTimestamp, setCheckInTimestamp] = useState(null);
    const itemsPerPage = 10;
    const [showManualPunch, setShowManualPunch] = useState(false);
    const [manualMessage, setManualMessage] = useState("");
    const [manualTime, setManualTime] = useState("");

    const ProfileAvatar = ({ url, name, className }) => {
        const initials = name
            ?.split(" ")
            .map((n) => n[0])
            .join("")
            .toUpperCase();

        return url ? (
            <img
                src={url}
                alt={name}
                className={`rounded-full object-cover ${className}`}
            />
        ) : (
            <div
                className={`rounded-full bg-gray-300 flex items-center justify-center text-sm font-bold text-gray-700 ${className}`}
            >
                {initials}
            </div>
        );
    };

    // Track which image is selected
    const [selectedImage, setSelectedImage] = useState(null);

    const handleImageClick = (url) => {
        if (url) setSelectedImage(url);
    };

    const closeModal = () => {
        setSelectedImage(null);
    };

    // Single useEffect for token and check-in state
    useEffect(() => {
        const token = localStorage.getItem("token");
        const storedUserId = localStorage.getItem("userId");
        const storedCheckInState = localStorage.getItem("isCheckedIn");

        if (storedCheckInState) {
            setIsCheckedIn(storedCheckInState === "true");
        }

        if (token) {
            try {
                const decodedToken = jwtDecode(token);
                setRole(decodedToken.role);
                setUserId(storedUserId);
                setEmployeeFilter([storedUserId]);
                setWebStatus(Number(decodedToken.webStatus));
            } catch (error) {
                console.error("Error decoding token:", error);
            }
        }
    }, []);

    // Ensure attendanceData is valid
    const validAttendanceData = Array.isArray(attendanceData)
        ? attendanceData
        : [];

    const totalAttendanceRecords = validAttendanceData.flatMap((employee) => {
        if (!Array.isArray(employee.attendance)) return [];

        return employee.attendance.map((record) => {
            const mappedRecord = {
                ...record, // Keep attendance details
                employeeId: employee.user_id,
                employeeName: employee.first_name
                    ? employee.last_name
                        ? `${employee.first_name} ${employee.last_name}`
                        : employee.first_name
                    : "N/A",
                employeeProfile: employee.user_profile_img,
            };

            return mappedRecord;
        });
    });

    const totalItems = totalAttendanceRecords.length;
    const totalPages = Math.ceil(totalItems / itemsPerPage);

    const indexOfFirstItem = (currentPage - 1) * itemsPerPage;
    const indexOfLastItem = indexOfFirstItem + itemsPerPage;

    // Paginate attendance records
    const currentAttendanceData = totalAttendanceRecords.slice(
        indexOfFirstItem,
        indexOfLastItem
    );

    // Fetch attendance data
    useEffect(() => {
        if (employeeFilter.length === 0) {
            setAttendanceData([]);
            setCurrentPage(1);
            return;
        }

        let url = `/api/attendance/getattendanceListByDate`;
        const params = {};

        if (employeeFilter.length > 0) {
            params.userId = employeeFilter.join(",");
        }

        if (dateFilter.startDate && dateFilter.endDate) {
            params.startdate = new Date(dateFilter.startDate)
                .toISOString()
                .split("T")[0];
            params.enddate = new Date(dateFilter.endDate).toISOString().split("T")[0];
        }

        if (selectedMonth) {
            params.month = selectedMonth;
        }

        setLoading(true);
        axiosInstance
            .get(url, { params })
            .then((response) => {
                if (response.data?.status && response.data.data.length > 0) {
                    console.log("Attendance data:", response.data.data);
                    setAttendanceData(response.data.data);
                    setCurrentPage(1);
                } else {
                    setAttendanceData([]);
                }
            })
            .catch((error) => {
                console.error("Error fetching data:", error);
                setAttendanceData([]);
            })
            .finally(() => {
                setLoading(false);
            });
    }, [dateFilter, employeeFilter, selectedMonth]);

    // Handle view restrictions
    useEffect(() => {
        if (employeeFilter.length > 1 && activeView === "calendar") {
            setActiveView("list");
        }
    }, [employeeFilter, activeView]);

    const handleDateSelected = (startDate, endDate, label) => {
        setDateFilter({ startDate, endDate });
        setDateRangeLabel(label);
    };

    const handleViewChange = (view) => {
        if (employeeFilter.length > 1 && (view === "calendar" || view === "timeline")) {
            toast.error(`Cannot show ${view} view for multiple employees`);
            return;
        }
        if (view === "timeline" && !employeeFilter[0]) {
            toast.error("Please select an employee first");
            return;
        }
        setActiveView(view);
    };

    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= totalPages) {
            setCurrentPage(newPage);
        }
    };

    const formatWorkingHours = (totalMinutes) => {
        if (!totalMinutes || totalMinutes <= 0) return "0 min";

        const hours = Math.floor(totalMinutes / 60);
        const minutes = totalMinutes % 60;

        if (hours > 0 && minutes > 0) {
            return `${hours} hr ${minutes} min`;
        } else if (hours > 0) {
            return `${hours} hr`;
        } else {
            return `${minutes} min`;
        }
    };

    const handleSort = () => {
        setSortConfig((prev) => ({
            key: "first_name",
            direction: prev.direction === "asc" ? "desc" : "asc",
        }));
    };

    // Sort attendance data when sortConfig changes
    useEffect(() => {
        setAttendanceData((prevEmployees) =>
            [...prevEmployees].sort((a, b) => {
                if (sortConfig.key !== "first_name") return 0;

                let valA = a.first_name?.toLowerCase() || "";
                let valB = b.first_name?.toLowerCase() || "";

                return sortConfig.direction === "asc"
                    ? valA.localeCompare(valB)
                    : valB.localeCompare(valA);
            })
        );
    }, [sortConfig]);

    const formatTimeDifference = (checkInTimestamp) => {
        if (!checkInTimestamp) return "N/A";
        const currentTimestamp = Math.floor(Date.now() / 1000);
        const differenceInSeconds = currentTimestamp - checkInTimestamp;
        const hours = Math.floor(differenceInSeconds / 3600);
        const minutes = Math.floor((differenceInSeconds % 3600) / 60);
        const seconds = differenceInSeconds % 60;
        return `${hours}h ${minutes}m ${seconds}s`;
    };

    // Update check-in timer
    useEffect(() => {
        let interval;
        if (isCheckedIn && checkInTimestamp) {
            interval = setInterval(() => {
                setCheckInTime(formatTimeDifference(checkInTimestamp));
            }, 1000);
        }
        return () => clearInterval(interval);
    }, [isCheckedIn, checkInTimestamp]);

    const handleCheckIn = async () => {
        try {
            const response = await axiosInstance.post(`/api/attendance/check-in`, {
                user_id_FK: userId,
            });

            if (response.data.status) {
                const newCheckInState = !isCheckedIn;
                const timestamp = Math.floor(Date.now() / 1000);

                // Update state immediately
                setIsCheckedIn(newCheckInState);
                setCheckInTimestamp(newCheckInState ? timestamp : null);

                // Update localStorage
                localStorage.setItem("isCheckedIn", newCheckInState.toString());

                toast.success(
                    newCheckInState
                        ? `Successfully checked in at ${new Date(timestamp * 1000).toLocaleTimeString()}!`
                        : "Successfully checked out!"
                );
            } else {
                toast.error(response.data.message || "Check-in failed");
            }
        } catch (error) {
            console.error("Check-in Error:", error);
            toast.error("Shift not created. Please contact the admin.");
        }
    };

    const handleManualCheckIn = async () => {
        if (!employeeFilter || employeeFilter.length !== 1 || !employeeFilter[0]) {
            toast.error("Please select exactly one employee from the dropdown first to mark attendance manually.");
            return;
        }
        try {
            // Build unix timestamp from selected time for TODAY.
            let unixLogTime = Math.floor(Date.now() / 1000);
            if (manualTime) {
                const [hours, minutes] = manualTime.split(":");
                const today = new Date();
                today.setHours(parseInt(hours, 10), parseInt(minutes, 10), 0, 0);
                unixLogTime = Math.floor(today.getTime() / 1000);
            }

            const response = await axiosInstance.post(`/api/attendance/manual-punch`, {
                user_id_FK: employeeFilter[0],
                message: manualMessage,
                log_time: unixLogTime
            });

            if (response.data.status) {
                toast.success(`Manual Check-In successful for ${userName}! Message saved: ${manualMessage}`);
                setShowManualPunch(false);
                setManualMessage("");
                setManualTime("");
            } else {
                toast.error(response.data.message || "Manual Check-in failed");
            }
        } catch (error) {
            console.error("Manual Check-in Error:", error);
            toast.error("Failed to mark manual attendance. Please contact admin.");
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
            <Navbar />
            <div className="p-6">
                <Toaster position="top-right" reverseOrder={false} />
                <div className="max-w-7xl mx-auto">
                    {/* Header */}
                    <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 rounded-2xl shadow-2xl p-8  text-white bg-gradient-to-r from-blue-600 via-blue-700 to-indigo-800  mb-6">

                        <div className="flex items-center gap-4">
                            {/* Icon Box */}
                            {(role === 1 || role === 2) && (
                                <div className="bg-white/20 p-3 rounded-lg flex items-center justify-center">
                                    <Layers size={32} className="text-white" />
                                </div>
                            )}

                            <div className="flex flex-col gap-3">
                                {/* Title for Role 1 & 2 */}
                                {(role === 1 || role === 2) && (
                                    <div className="flex items-center gap-4">
                                        <h2 className="text-xl md:text-2xl font-semibold font-inter text-white">
                                            Employee Management
                                        </h2>
                                        {employeeFilter?.length === 1 && employeeFilter[0] && (
                                            <button
                                                type="button"
                                                onClick={() => setShowManualPunch(true)}
                                                className="bg-yellow-500 hover:bg-yellow-600 px-4 py-2 text-sm font-semibold rounded shadow-md text-white flex gap-2 items-center"
                                            >
                                                <Plus size={16} /> Mark Check-In
                                            </button>
                                        )}
                                    </div>
                                )}

                                {/* Check-In / Check-Out Button for Role 3 */}
                                {role === 3 && webStatus === 0 && (
                                    <button
                                        type="button"
                                        onClick={handleCheckIn}
                                        className={`w-40 h-14 px-4 rounded-full shadow-lg transition-all duration-300 flex items-center gap-3 backdrop-blur-md
                    ${isCheckedIn
                                                ? "bg-gradient-to-r from-red-500 to-red-700 hover:from-red-600 hover:to-red-800"
                                                : "bg-gradient-to-r from-green-500 to-green-700 hover:from-green-600 hover:to-green-800"
                                            }
                `}
                                    >
                                        {/* Clock Icon */}
                                        <div className="flex items-center justify-center w-8 h-8 rounded-full bg-white/20">
                                            <FaRegClock className="text-white text-sm" />
                                        </div>

                                        {/* Button Text */}
                                        <div className="flex flex-col text-left text-white leading-tight">
                                            <span className="text-sm font-semibold">
                                                {isCheckedIn ? "Check-Out" : "Check-In"}
                                            </span>

                                            {isCheckedIn && checkInTime && (
                                                <span className="text-xs opacity-90">
                                                    {checkInTime}
                                                </span>
                                            )}
                                        </div>
                                    </button>
                                )}
                            </div>
                        </div>

                        {/* Right side — Dropdowns and Filters */}
                        <div className="flex flex-wrap items-center gap-4">
                            {role !== 3 ? (
                                <EmployeeDropdown
                                    onEmployeeSelected={(selectedUsers) => {
                                        setEmployeeFilter(selectedUsers);
                                        setActiveView("list");
                                    }}
                                    selectedEmployeeId={userId}
                                    onUserNameRetrieved={setUserName}
                                />
                            ) : null}

                            {/* Date Range Picker */}
                            {activeView === "list" && (role === 1 || role === 2 || role === 3) && (
                                <DateRangePickerComponent onDateSelected={handleDateSelected} />
                            )}

                            {/* Employee Attendance Download */}
                            {(role === 1 || role === 2) && (
                                <EmployeeAttendanceDropdown
                                    employeeFilter={employeeFilter}
                                    dateFilter={dateFilter}
                                    selectedMonth={selectedMonth}
                                />
                            )}
                        </div>
                    </div>

                    {/* View Icons Section */}
                    {employeeFilter.length === 1 && (
                        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 py-4 mb-6 bg-white rounded-lg px-4 shadow-sm">
                            <span className="text-lg font-semibold text-gray-800">
                                👤 {userName || "Selected Employee"}
                            </span>

                            <div className="flex items-center gap-4">
                                <FaList
                                    className={`text-2xl cursor-pointer transition ${activeView === "list"
                                        ? "text-blue-600 scale-110"
                                        : "text-gray-700 hover:text-blue-600"
                                        }`}
                                    onClick={() => handleViewChange("list")}
                                    title="List View"
                                />

                                <TbLayoutDashboardFilled
                                    className={`text-2xl cursor-pointer transition ${activeView === "calendar"
                                        ? "text-blue-600 scale-110"
                                        : "text-gray-700 hover:text-blue-600"
                                        }`}
                                    onClick={() => handleViewChange("calendar")}
                                    title="Calendar View"
                                />

                            </div>
                        </div>
                    )}

                    {/* TABLE CONTAINER */}
                    <div className="min-h-screen flex flex-col">
                        <div className="flex-1 overflow-hidden">
                            <div className="w-full bg-white shadow-md rounded-lg overflow-hidden">

                                {/* Selected Date Range */}
                                {dateRangeLabel && activeView === "list" && (
                                    <div className="px-4 py-3 text-lg font-medium text-gray-900 bg-gray-50 border-b">
                                        Selected Date Range: {dateRangeLabel}
                                    </div>
                                )}

                                {activeView === "list" && (
                                    <div className="w-full overflow-hidden">
                                        <table className="w-full table-fixed bg-white rounded-xl mt-3">

                                            {/* TABLE HEAD */}
                                            <thead className="bg-gradient-to-r from-blue-600 via-blue-700 to-indigo-800 text-white">
                                                <tr>
                                                    <th className="px-3 py-3 text-sm text-left font-semibold w-[50px] text-white">
                                                        S.No
                                                    </th>

                                                    <th
                                                        className="px-3 py-3 text-sm text-left font-semibold cursor-pointer text-white"
                                                        onClick={handleSort}
                                                    >
                                                        Name {sortConfig.direction === "asc" ? "▲" : "▼"}
                                                    </th>

                                                    <th className="px-3 py-3 text-sm text-left font-semibold whitespace-nowrap text-white">
                                                        Date
                                                    </th>

                                                    <th className="px-3 py-3 text-sm text-left font-semibold whitespace-nowrap text-white">
                                                        Check-In
                                                    </th>

                                                    <th className="px-3 py-3 text-sm text-left font-semibold whitespace-nowrap text-white">
                                                        Check-Out
                                                    </th>

                                                    <th className="px-3 py-3 text-sm text-left font-semibold text-white">
                                                        Office
                                                    </th>

                                                    <th className="px-3 py-3 text-sm text-left font-semibold text-white">
                                                        Working
                                                    </th>

                                                    <th className="px-3 py-3 text-sm text-left font-semibold text-white">
                                                        Break
                                                    </th>

                                                    <th className="px-3 py-3 text-sm text-left font-semibold text-white">
                                                        Productive
                                                    </th>

                                                    <th className="px-3 py-3 text-sm text-left font-semibold text-white">
                                                        Status
                                                    </th>
                                                </tr>
                                            </thead>

                                            {/* TABLE BODY */}
                                            <tbody className="divide-y divide-gray-200">
                                                {loading ? (
                                                    <tr>
                                                        <td colSpan={10} className="py-12 text-center">
                                                            <Spinner size={30} loading />
                                                        </td>
                                                    </tr>
                                                ) : currentAttendanceData.length > 0 ? (
                                                    currentAttendanceData.map((record, index) => (
                                                        <tr
                                                            key={`${record.employeeId}-${record.date}-${index}`}
                                                            className="hover:bg-gray-50 transition"
                                                        >
                                                            <td className="px-3 py-3 text-center text-sm">
                                                                {indexOfFirstItem + index + 1}
                                                            </td>
                                                            <td className=" py-3 font-inter text-sm whitespace-nowrap">
                                                                <div className="flex items-center space-x-3">
                                                                    <div
                                                                        className="cursor-pointer flex-shrink-0"
                                                                        onClick={() => handleImageClick(record.employeeProfile)}
                                                                    >
                                                                        <ProfileAvatar
                                                                            url={record.employeeProfile}
                                                                            name={record.employeeName}
                                                                            className="h-8 w-8 md:h-9 md:w-9"
                                                                        />
                                                                    </div>
                                                                    <div className="min-w-0 flex-1">
                                                                        <span className="font-medium  truncate block">
                                                                            {record.employeeName}
                                                                        </span>
                                                                    </div>
                                                                </div>
                                                            </td>


                                                            {/* DATE */}
                                                            <td className="px-3 py-3 text-sm whitespace-nowrap">
                                                                {record.date
                                                                    ? new Date(record.date).toLocaleDateString("en-GB", {
                                                                        day: "2-digit",
                                                                        month: "short",
                                                                        year: "numeric",
                                                                    })
                                                                    : "-"}
                                                            </td>

                                                            {/* CHECK-IN */}
                                                            <td className="px-3 py-3 text-sm whitespace-nowrap">
                                                                {record.check_in_time
                                                                    ? new Date(record.check_in_time * 1000).toLocaleTimeString([], {
                                                                        hour: "2-digit",
                                                                        minute: "2-digit",
                                                                        hour12: true,
                                                                    })
                                                                    : "-"}
                                                            </td>

                                                            {/* CHECK-OUT */}
                                                            <td className="px-3 py-3 text-sm whitespace-nowrap">
                                                                {record.check_out_time &&
                                                                    record.check_out_time !== record.check_in_time
                                                                    ? new Date(record.check_out_time * 1000).toLocaleTimeString([], {
                                                                        hour: "2-digit",
                                                                        minute: "2-digit",
                                                                        hour12: true,
                                                                    })
                                                                    : "-"}
                                                            </td>

                                                            <td className="px-3 py-3 text-sm">9:00 hr</td>
                                                            <td className="px-3 py-3 text-sm">8:00 hr</td>
                                                            <td className="px-3 py-3 text-sm">1:00 hr</td>

                                                            <td className="px-3 py-3 text-sm">
                                                                {record.total_working_hours
                                                                    ? formatWorkingHours(record.total_working_hours)
                                                                    : "0 min"}
                                                            </td>

                                                            {/* STATUS */}
                                                            <td className="px-4 py-3 font-inter text-sm whitespace-nowrap">
                                                                {(() => {
                                                                    const statusMap = {
                                                                        1: { text: "Present", color: "bg-green-100 text-green-800 border border-green-200" },
                                                                        2: { text: "Absent", color: "bg-red-100 text-red-800 border border-red-200" },
                                                                        3: { text: "Holiday", color: "bg-blue-100 text-blue-800 border border-blue-200" },
                                                                        4: { text: "Leave", color: "bg-purple-100 text-purple-800 border border-purple-200" },
                                                                        5: { text: "Holiday But Present", color: "" },
                                                                        6: { text: "Leave But Present", color: "" },
                                                                        7: { text: "Weekend", color: "bg-yellow-100 text-yellow-800 border border-yellow-200" },
                                                                        8: { text: "Weekend But Present", color: "" },
                                                                    };

                                                                    const lateStatusMap = {
                                                                        0: { text: "On Time", color: "bg-green-100 text-green-800 border border-green-200" },
                                                                        1: { text: "Late", color: "bg-orange-100 text-orange-800 border border-orange-200" },
                                                                        2: { text: "Half Day", color: "bg-amber-100 text-amber-800 border border-amber-200" },
                                                                    };

                                                                    const renderBadges = () => {
                                                                        switch (record.status) {
                                                                            case 5:
                                                                                return (
                                                                                    <div className="flex flex-col gap-1">
                                                                                        <span className="inline-flex items-center rounded-lg px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 border border-blue-200">
                                                                                            Holiday
                                                                                        </span>
                                                                                        <span className="inline-flex items-center rounded-lg px-2 py-1 text-xs font-medium bg-green-100 text-green-800 border border-green-200">
                                                                                            Present
                                                                                        </span>
                                                                                    </div>
                                                                                );
                                                                            case 6:
                                                                                return (
                                                                                    <div className="flex flex-col gap-1">
                                                                                        <span className="inline-flex items-center rounded-lg px-2 py-1 text-xs font-medium bg-purple-100 text-purple-800 border border-purple-200">
                                                                                            Leave
                                                                                        </span>
                                                                                        <span className="inline-flex items-center rounded-lg px-2 py-1 text-xs font-medium bg-green-100 text-green-800 border border-green-200">
                                                                                            Present
                                                                                        </span>
                                                                                    </div>
                                                                                );
                                                                            case 8:
                                                                                return (
                                                                                    <div className="flex flex-col gap-1">
                                                                                        <span className="inline-flex items-center rounded-lg px-2 py-1 text-xs font-medium bg-yellow-100 text-yellow-800 border border-yellow-200">
                                                                                            Weekend
                                                                                        </span>
                                                                                        <span className="inline-flex items-center rounded-lg px-2 py-1 text-xs font-medium bg-green-100 text-green-800 border border-green-200">
                                                                                            Present
                                                                                        </span>
                                                                                    </div>
                                                                                );
                                                                            default:
                                                                                const { text, color } = statusMap[record.status] || {
                                                                                    text: "Unknown",
                                                                                    color: "bg-gray-100 text-gray-800 border border-gray-200",
                                                                                };
                                                                                return (
                                                                                    <span className={`inline-flex items-center rounded-lg px-2 py-1 text-xs font-medium ${color}`}>
                                                                                        {text}
                                                                                    </span>
                                                                                );
                                                                        }
                                                                    };

                                                                    const lateStatus = lateStatusMap[record.late_status] || {
                                                                        text: "Unknown",
                                                                        color: "bg-gray-100 text-gray-800 border border-gray-200",
                                                                    };

                                                                    return (
                                                                        <div className="flex flex-col gap-1">
                                                                            {renderBadges()}
                                                                            <span className={`inline-flex items-center rounded-lg px-2 py-1 text-xs font-medium ${lateStatus.color}`}>
                                                                                {lateStatus.text}
                                                                            </span>
                                                                        </div>
                                                                    );
                                                                })()}
                                                            </td>
                                                        </tr>
                                                    ))
                                                ) : (
                                                    <tr>
                                                        <td colSpan={10} className="py-10 text-center text-gray-500">
                                                            No attendance data found
                                                        </td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>

                                )}
                            </div>
                            {activeView === "calendar" && employeeFilter.length > 0 && (
                                <CalenderView userId={employeeFilter[0]} />
                            )}

                            {activeView === "timeline" && employeeFilter.length === 1 && (
                                <TimeLineView userId={employeeFilter[0]} />
                            )}
                        </div>

                        {/* PAGINATION */}
                        {activeView === "list" && totalItems > 0 && (
                            <div className="w-full mt-6 bg-white rounded-lg shadow-sm p-4">
                                <Pagination
                                    totalItems={totalItems}
                                    itemsPerPage={itemsPerPage}
                                    currentPage={currentPage}
                                    onPageChange={handlePageChange}
                                />
                            </div>
                        )}
                    </div>


                    {/* <div className="min-h-screen flex flex-col">
                        <div className="flex-1 overflow-x-auto">
                            <div className="min-w-full bg-white shadow-md rounded-lg overflow-hidden">
                                {dateRangeLabel && activeView === "list" && (
                                    <div className="px-4 py-3 text-lg font-medium text-gray-900 bg-gray-50 border-b">
                                        Selected Date Range: {dateRangeLabel}
                                    </div>
                                )}

                                {activeView === "list" && (
                                    <div className="w-full h-full overflow-x-auto">
                                        <table className="min-w-full bg-white shadow-md rounded-xl lg:mt-4 mt-3">
                                            <thead className="bg-gradient-to-r from-blue-600 via-blue-700 to-indigo-800 text-white">
                                                <tr>
                                                    <th className="py-3 px-4 w-[60px] font-inter text-left text-sm whitespace-nowrap font-semibold text-white">
                                                        S.No.
                                                    </th>
                                                    <th
                                                        className="py-3 px-4 w-[250px] font-inter text-left text-sm whitespace-nowrap font-semibold cursor-pointer text-white"
                                                        onClick={handleSort}
                                                    >
                                                        Name {sortConfig.direction === "asc" ? "▲" : "▼"}
                                                    </th>
                                                    <th className="py-3 px-4 w-[140px] font-inter text-left text-sm whitespace-nowrap font-semibold text-white">
                                                        Date
                                                    </th>
                                                    <th className="py-3 px-4 w-[130px] font-inter text-left text-sm whitespace-nowrap font-semibold text-white">
                                                        Check-in
                                                    </th>
                                                    <th className="py-3 px-4 w-[130px] font-inter text-left text-sm whitespace-nowrap font-semibold text-white">
                                                        Check-out
                                                    </th>
                                                    <th className="py-3 px-4 w-[140px] font-inter text-left text-sm whitespace-nowrap font-semibold text-white">
                                                        Office Hours
                                                    </th>
                                                    <th className="py-3 px-4 w-[140px] font-inter text-left text-sm whitespace-nowrap font-semibold text-white">
                                                        Working Hours
                                                    </th>
                                                    <th className="py-3 px-4 w-[130px] font-inter text-left text-sm whitespace-nowrap font-semibold text-white">
                                                        Break Time
                                                    </th>
                                                    <th className="py-3 px-4 w-[150px] font-inter text-left text-sm whitespace-nowrap font-semibold text-white">
                                                        Productive Hours
                                                    </th>
                                                    <th className="py-3 px-4 w-[180px] font-inter text-left text-sm whitespace-nowrap font-semibold text-white">
                                                        Status
                                                    </th>
                                                </tr>
                                            </thead>

                                            <tbody className="divide-y divide-gray-200">
                                                {loading ? (
                                                    <tr>
                                                        <td colSpan="10" className="text-center py-12">
                                                            <Spinner size={30} color="#3498db" loading={loading} />
                                                        </td>
                                                    </tr>
                                                ) : totalAttendanceRecords.length > 0 ? (
                                                    currentAttendanceData.map((record, index) => (
                                                        <tr
                                                            key={`${record.employeeId}-${record.date}-${index}`}
                                                            className="hover:bg-gray-50 transition-colors duration-150"
                                                        >
                                                            <td className="px-4 py-3 font-inter text-sm whitespace-nowrap text-center">
                                                                {indexOfFirstItem + index + 1}
                                                            </td>
                                                            <td className="px-4 py-3 font-inter text-sm whitespace-nowrap">
                                                                <div className="flex items-center space-x-3">
                                                                    <div
                                                                        className="cursor-pointer flex-shrink-0"
                                                                        onClick={() => handleImageClick(record.employeeProfile)}
                                                                    >
                                                                        <ProfileAvatar
                                                                            url={record.employeeProfile}
                                                                            name={record.employeeName}
                                                                            className="h-8 w-8 md:h-10 md:w-10"
                                                                        />
                                                                    </div>
                                                                    <div className="min-w-0 flex-1">
                                                                        <span className="font-medium  truncate block">
                                                                            {record.employeeName}
                                                                        </span>
                                                                    </div>
                                                                </div>
                                                            </td>
                                                            <td className="px-4 py-3 font-inter text-sm whitespace-nowrap">
                                                                {record.date
                                                                    ? new Date(record.date)
                                                                        .toLocaleDateString("en-GB", {
                                                                            day: "2-digit",
                                                                            month: "short",
                                                                            year: "numeric",
                                                                        })
                                                                        .replace(",", "")
                                                                    : "N/A"}
                                                            </td>
                                                            <td className="px-4 py-3 font-inter text-sm whitespace-nowrap">
                                                                {record.check_in_time
                                                                    ? new Date(record.check_in_time * 1000).toLocaleTimeString([], {
                                                                        hour: "2-digit",
                                                                        minute: "2-digit",
                                                                        hour12: true,
                                                                    })
                                                                    : "-"}
                                                            </td>
                                                            <td className="px-4 py-3 font-inter text-sm whitespace-nowrap">
                                                                {record.check_out_time
                                                                    ? record.check_in_time === record.check_out_time
                                                                        ? "-"
                                                                        : new Date(record.check_out_time * 1000).toLocaleTimeString([], {
                                                                            hour: "2-digit",
                                                                            minute: "2-digit",
                                                                            hour12: true,
                                                                        })
                                                                    : "-"}
                                                            </td>
                                                            <td className="px-4 py-3 font-inter text-sm whitespace-nowrap">
                                                                9:00 hr
                                                            </td>
                                                            <td className="px-4 py-3 font-inter text-sm whitespace-nowrap">
                                                                8:00 hr
                                                            </td>
                                                            <td className="px-4 py-3 font-inter text-sm whitespace-nowrap">
                                                                1:00 hr
                                                            </td>
                                                            <td className="px-4 py-3 font-inter text-sm whitespace-nowrap">
                                                                {record.total_working_hours
                                                                    ? formatWorkingHours(record.total_working_hours)
                                                                    : "0 min"}
                                                            </td>
                                                            <td className="px-4 py-3 font-inter text-sm whitespace-nowrap">
                                                                {(() => {
                                                                    const statusMap = {
                                                                        1: { text: "Present", color: "bg-green-100 text-green-800 border border-green-200" },
                                                                        2: { text: "Absent", color: "bg-red-100 text-red-800 border border-red-200" },
                                                                        3: { text: "Holiday", color: "bg-blue-100 text-blue-800 border border-blue-200" },
                                                                        4: { text: "Leave", color: "bg-purple-100 text-purple-800 border border-purple-200" },
                                                                        5: { text: "Holiday But Present", color: "" },
                                                                        6: { text: "Leave But Present", color: "" },
                                                                        7: { text: "Weekend", color: "bg-yellow-100 text-yellow-800 border border-yellow-200" },
                                                                        8: { text: "Weekend But Present", color: "" },
                                                                    };

                                                                    const lateStatusMap = {
                                                                        0: { text: "On Time", color: "bg-green-100 text-green-800 border border-green-200" },
                                                                        1: { text: "Late", color: "bg-orange-100 text-orange-800 border border-orange-200" },
                                                                        2: { text: "Half Day", color: "bg-amber-100 text-amber-800 border border-amber-200" },
                                                                    };

                                                                    const renderBadges = () => {
                                                                        switch (record.status) {
                                                                            case 5:
                                                                                return (
                                                                                    <div className="flex flex-col gap-1">
                                                                                        <span className="inline-flex items-center rounded-lg px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 border border-blue-200">
                                                                                            Holiday
                                                                                        </span>
                                                                                        <span className="inline-flex items-center rounded-lg px-2 py-1 text-xs font-medium bg-green-100 text-green-800 border border-green-200">
                                                                                            Present
                                                                                        </span>
                                                                                    </div>
                                                                                );
                                                                            case 6:
                                                                                return (
                                                                                    <div className="flex flex-col gap-1">
                                                                                        <span className="inline-flex items-center rounded-lg px-2 py-1 text-xs font-medium bg-purple-100 text-purple-800 border border-purple-200">
                                                                                            Leave
                                                                                        </span>
                                                                                        <span className="inline-flex items-center rounded-lg px-2 py-1 text-xs font-medium bg-green-100 text-green-800 border border-green-200">
                                                                                            Present
                                                                                        </span>
                                                                                    </div>
                                                                                );
                                                                            case 8:
                                                                                return (
                                                                                    <div className="flex flex-col gap-1">
                                                                                        <span className="inline-flex items-center rounded-lg px-2 py-1 text-xs font-medium bg-yellow-100 text-yellow-800 border border-yellow-200">
                                                                                            Weekend
                                                                                        </span>
                                                                                        <span className="inline-flex items-center rounded-lg px-2 py-1 text-xs font-medium bg-green-100 text-green-800 border border-green-200">
                                                                                            Present
                                                                                        </span>
                                                                                    </div>
                                                                                );
                                                                            default:
                                                                                const { text, color } = statusMap[record.status] || {
                                                                                    text: "Unknown",
                                                                                    color: "bg-gray-100 text-gray-800 border border-gray-200",
                                                                                };
                                                                                return (
                                                                                    <span className={`inline-flex items-center rounded-lg px-2 py-1 text-xs font-medium ${color}`}>
                                                                                        {text}
                                                                                    </span>
                                                                                );
                                                                        }
                                                                    };

                                                                    const lateStatus = lateStatusMap[record.late_status] || {
                                                                        text: "Unknown",
                                                                        color: "bg-gray-100 text-gray-800 border border-gray-200",
                                                                    };

                                                                    return (
                                                                        <div className="flex flex-col gap-1">
                                                                            {renderBadges()}
                                                                            <span className={`inline-flex items-center rounded-lg px-2 py-1 text-xs font-medium ${lateStatus.color}`}>
                                                                                {lateStatus.text}
                                                                            </span>
                                                                        </div>
                                                                    );
                                                                })()}
                                                            </td>
                                                        </tr>
                                                    ))
                                                ) : (
                                                    <tr>
                                                        <td colSpan="10" className="text-center py-8 text-gray-500">
                                                            <div className="flex flex-col items-center justify-center space-y-2">
                                                                <div className="text-gray-400">
                                                                    <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                                                    </svg>
                                                                </div>
                                                                <p className="text-lg font-medium">No attendance data found</p>
                                                                <p className="text-sm text-gray-400">
                                                                    {employeeFilter.length === 0
                                                                        ? "Please select an employee to view attendance"
                                                                        : "Try adjusting your filters or date range"}
                                                                </p>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                )}

                                {activeView === "calendar" && employeeFilter.length > 0 && (
                                    <CalenderView userId={employeeFilter[0]} />
                                )}

                                {activeView === "timeline" && employeeFilter.length === 1 && (
                                    <TimeLineView userId={employeeFilter[0]} />
                                )}
                            </div>
                        </div>

                        {activeView === "list" && totalItems > 0 && (
                            <div className="w-full mt-6 bg-white rounded-lg shadow-sm p-4">
                                <Pagination
                                    totalItems={totalItems}
                                    itemsPerPage={itemsPerPage}
                                    currentPage={currentPage}
                                    onPageChange={handlePageChange}
                                />
                            </div>
                        )}
                    </div> */}




                </div>
            </div>

            {/* Image Modal */}
            {selectedImage && (
                <div
                    className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50"
                    onClick={closeModal}
                >
                    <div
                        className="relative"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <img
                            src={selectedImage}
                            alt="Large profile"
                            className="max-w-[90vw] max-h-[90vh] rounded-lg shadow-lg"
                        />
                        <button
                            onClick={closeModal}
                            className="absolute top-2 right-2 bg-white rounded-full px-3 py-1 text-black font-bold hover:bg-gray-100"
                        >
                            ✕
                        </button>
                    </div>
                </div>
            )}

            {/* Manual Punch Modal */}
            {showManualPunch && (
                <div
                    className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4"
                    onClick={() => setShowManualPunch(false)}
                >
                    <div
                        className="bg-white rounded-lg shadow-xl w-full max-w-md overflow-hidden relative"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="bg-gradient-to-r from-blue-600 to-indigo-700 px-6 py-4 flex justify-between items-center">
                            <h3 className="text-lg font-semibold text-white">Manual Check-In</h3>
                            <button onClick={() => setShowManualPunch(false)} className="text-white hover:text-gray-200 font-bold text-xl">✕</button>
                        </div>
                        <div className="p-6">
                            <p className="mb-4 text-gray-700 font-medium">Recording attendance for: <strong>{userName}</strong></p>

                            <label className="block text-sm font-medium text-gray-700 mb-2">Check-in Time (Overrides current time)</label>
                            <input
                                type="time"
                                className="w-full border border-gray-300 rounded-md p-3 mb-4 focus:ring-blue-500 focus:border-blue-500"
                                value={manualTime}
                                onChange={(e) => setManualTime(e.target.value)}
                            />

                            <label className="block text-sm font-medium text-gray-700 mb-2">Message / Reason (e.g. Forgot card)</label>
                            <textarea
                                className="w-full border border-gray-300 rounded-md p-3 mb-4 focus:ring-blue-500 focus:border-blue-500"
                                rows="3"
                                placeholder="Enter reason here..."
                                value={manualMessage}
                                onChange={(e) => setManualMessage(e.target.value)}
                            ></textarea>

                            <div className="flex justify-end gap-3 mt-2">
                                <button
                                    onClick={() => setShowManualPunch(false)}
                                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 focus:outline-none"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleManualCheckIn}
                                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                >
                                    Submit Punch
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}